/**
 * /night-vision — the GPU executor. Every stage of core.js as a WebGL2 pass,
 * formula for formula (tests/playwright/night-vision-gpu-parity.mjs renders
 * the same frames through both and compares).
 *
 * Per frame, all on the GPU, nothing waits for the CPU:
 *   curHalf  the new frame at half size (luma) + mipmaps      — what the camera sees now
 *   cost     shake search at 1/16 size, ±3 texels, then ±1 at 1/4 size around
 *            the winner: the history is read where the scene WAS (no CPU
 *            round trip, so no frame of latency in the alignment)
 *   temporal the recursive average with variance clipping and the noise-model
 *            motion test (MRT: history + motion/aux)
 *   half     the averaged picture at half size + mipmaps    — local brightness, soft colour, exposure
 *   out      edge-aware filter, colour, exposure, tone, look, overlay → canvas
 *   stats    a small grid read back asynchronously (PBO + fence) for the
 *            gain, the noise meter, motion watch and the infrared test
 *
 * Storage. History is RGBA16F where the GPU can render to half floats
 * (EXT_color_buffer_float, nearly everywhere), else RGBA16UI with dithered
 * rounding — integer formats are renderable in core WebGL2. Every read of the
 * history is texelFetch, so both storages share one shader. Without float
 * targets the shake search is skipped (shift 0) and the half-size copy is
 * stored as √value in bytes.
 */
import { paletteLUT, PARAMS, localLevelFor, alignLevels, ALIGN_PRIOR } from './core.js';

const VS = `#version 300 es
const vec2 P[3] = vec2[3](vec2(-1.0,-1.0), vec2(3.0,-1.0), vec2(-1.0,3.0));
void main(){ gl_Position = vec4(P[gl_VertexID], 0.0, 1.0); }`;

const COMMON = `
precision highp float; precision highp int; precision highp sampler2D;
uniform vec2 uSize;          // processing size (W,H)
uniform mat3 uSrc;           // image uv (top-left origin) → source texture uv
uniform sampler2D uIn;       // the camera frame, sRGB bytes, LINEAR filter
vec3 s2l(vec3 c){ return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c)); }
vec3 l2s(vec3 c){ c = clamp(c, 0.0, 1.0); return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c)); }
float luma(vec3 c){ return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
vec2 srcUv(vec2 pix){ return (uSrc * vec3(pix / uSize, 1.0)).xy; }
vec3 srcLin(vec2 pix){ return s2l(texture(uIn, srcUv(pix)).rgb); }
float hash(vec2 p, float t){ vec3 q = fract(vec3(p.xyx) * 0.1031 + t * 0.0137); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
`;

const ACC_DECL = `
#ifdef ACC_UINT
uniform highp usampler2D uAcc;
vec4 accAt(ivec2 p){ p = clamp(p, ivec2(0), ivec2(uSize) - 1); return vec4(texelFetch(uAcc, p, 0)) * vec4(1.0/65535.0, 1.0/65535.0, 1.0/65535.0, 1.0/256.0); }
#else
uniform highp sampler2D uAcc;
vec4 accAt(ivec2 p){ p = clamp(p, ivec2(0), ivec2(uSize) - 1); return texelFetch(uAcc, p, 0); }
#endif
`;

const HALF_DECL = `
uniform sampler2D uHalf;     // rgb linear, a luma; mipmapped
vec4 halfAt(vec2 uv, float lod){
#ifdef HALF_SQRT
    vec4 v = textureLod(uHalf, uv, lod); return v * v;
#else
    return textureLod(uHalf, uv, lod);
#endif
}
`;

/* curHalf: the new frame at half size, linear luma (r) — the shake search's «now». */
const FS_CURHALF = `#version 300 es
${COMMON}
out vec4 o;
void main(){
    vec2 p = floor(gl_FragCoord.xy) * 2.0;
    float l = luma(srcLin(p + vec2(0.5, 0.5))) + luma(srcLin(p + vec2(1.5, 0.5)))
            + luma(srcLin(p + vec2(0.5, 1.5))) + luma(srcLin(p + vec2(1.5, 1.5)));
    o = vec4(l * 0.25, 0.0, 0.0, 1.0);
}`;

/*
 * cost: one fragment per candidate shift; mean-normalised absolute difference
 * between «now» and the previous history at that shift, over a level of both
 * pyramids. uCenter is the search centre (level texels), the candidate is
 * centre + (fragment − R).
 */
const FS_COST = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uCur;      // curHalf (r = luma), mipmapped
uniform sampler2D uPrev;     // half (a = luma), mipmapped
uniform sampler2D uCenterTex;// 1×1: xy = centre in level texels (unused when uUseCenter = 0)
uniform float uUseCenter;
uniform float uCenterScale; // the centre comes from the coarser level: × its size ratio
uniform float uLod;
uniform int uR;
uniform float uTopCur, uTopPrev; // top mip level of each, for the means
uniform float uPrior;        // coarse level only: cost × (1 + prior · |s|²)
out vec4 o;
void main(){
    ivec2 sz = textureSize(uCur, int(uLod));
    vec2 szf = vec2(sz);
    vec2 c = uUseCenter > 0.5 ? texelFetch(uCenterTex, ivec2(0), 0).xy * uCenterScale : vec2(0.0);
    vec2 s = floor(c + 0.5) + vec2(ivec2(gl_FragCoord.xy) - ivec2(uR));
    float mc = max(textureLod(uCur, vec2(0.5), uTopCur).r, 1e-6);
    float mp = max(textureLod(uPrev, vec2(0.5), uTopPrev).a, 1e-6);
    int m = uR + 1;
    float e = 0.0, n = 0.0;
    for (int y = m; y < sz.y - m; y++) {
        for (int x = m; x < sz.x - m; x++) {
            float a = texelFetch(uCur, ivec2(x, y), int(uLod)).r / mc;
            vec2 pu = (vec2(float(x), float(y)) + 0.5 - s) / szf;
            float b = textureLod(uPrev, pu, uLod).a / mp;
            e += abs(a - b); n += 1.0;
        }
    }
    float pr = uUseCenter > 0.5 ? 1.0 : 1.0 + uPrior * dot(s, s);
    o = vec4(e / max(n, 1.0) * pr, s, 1.0);
}`;

/*
 * best: argmin over the cost grid, parabola through the neighbours → shift in
 * level texels. uSecond = 1: the runner-up from another basin (Chebyshev
 * distance ≥ 2 from the winner) — the second hypothesis of core.js shiftSearch.
 */
const FS_BEST = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uCost;
uniform int uS;
uniform int uSecond;
out vec4 o;
float par(float a, float b, float c){ float d = a - 2.0 * b + c; return d > 1e-9 ? clamp(0.5 * (a - c) / d, -0.5, 0.5) : 0.0; }
void main(){
    int bi = 0; float bc = 1e9, avg = 0.0;
    for (int i = 0; i < 64; i++) { if (i >= uS * uS) break; float c = texelFetch(uCost, ivec2(i % uS, i / uS), 0).r; avg += c; if (c < bc) { bc = c; bi = i; } }
    avg /= float(uS * uS);
    if (uSecond == 1) {
        int wx = bi % uS, wy = bi / uS, si = -1; float sc = 1e9;
        for (int i = 0; i < 64; i++) {
            if (i >= uS * uS) break;
            if (max(abs(i % uS - wx), abs(i / uS - wy)) < 2) continue;
            float c = texelFetch(uCost, ivec2(i % uS, i / uS), 0).r;
            if (si < 0 || c < sc) { sc = c; si = i; }
        }
        bi = si; bc = sc;
    }
    int bx = bi % uS, by = bi / uS;
    vec2 base = texelFetch(uCost, ivec2(bx, by), 0).gb;
    float fx = 0.0, fy = 0.0;
    if (bx > 0 && bx < uS - 1) fx = par(texelFetch(uCost, ivec2(bx - 1, by), 0).r, bc, texelFetch(uCost, ivec2(bx + 1, by), 0).r);
    if (by > 0 && by < uS - 1) fy = par(texelFetch(uCost, ivec2(bx, by - 1), 0).r, bc, texelFetch(uCost, ivec2(bx, by + 1), 0).r);
    float q = avg > 1e-9 ? clamp((avg - bc) / avg, 0.0, 1.0) : 0.0;
    o = vec4(base + vec2(fx, fy), q, 1.0);
}`;

/*
 * fine shake search: at the half-size level itself, ±1 around the 1/4-level
 * winner, every pixel at once — the 9 candidates' truncated squared
 * differences go to three MRT targets (9 costs + a weight that is 0 at the
 * border), and generateMipmap reduces them to means. Truncation: a thing
 * moving through the frame must not pull the camera's shift (core.js).
 * The targets are a power of two (an even sample of the half-size frame):
 * generateMipmap of an odd size drops rows — at 5×3 → 2×1 → 1×1 the «mean»
 * was the middle third of the frame only, and the shift came out 0.6 px off
 * on a still scene (parity stand, 23.10.2026).
 */
const FS_DIFF9 = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uCur, uPrev, uCenterTex;
uniform float uTopCur, uTopPrev, uTau2, uFineScale;
uniform vec2 uDiffSize;
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
layout(location = 2) out vec4 o2;
void main(){
    ivec2 sz = textureSize(uCur, 0);
    ivec2 p = ivec2((floor(gl_FragCoord.xy) + 0.5) * vec2(sz) / uDiffSize);
    o0 = vec4(0.0); o1 = vec4(0.0); o2 = vec4(0.0);
    if (p.x < 2 || p.y < 2 || p.x >= sz.x - 2 || p.y >= sz.y - 2) return;
    float mc = max(textureLod(uCur, vec2(0.5), uTopCur).r, 1e-6);
    float mp = max(textureLod(uPrev, vec2(0.5), uTopPrev).a, 1e-6);
    ivec2 c = ivec2(floor(texelFetch(uCenterTex, ivec2(0), 0).xy * uFineScale + 0.5));
    float a = texelFetch(uCur, p, 0).r / mc;
    float e[9];
    for (int k = 0; k < 9; k++) {
        ivec2 s = c + ivec2(k % 3 - 1, k / 3 - 1);
        float b = texelFetch(uPrev, clamp(p - s, ivec2(0), sz - 1), 0).a / mp;
        float d = a - b;
        e[k] = min(d * d, uTau2);
    }
    o0 = vec4(e[0], e[1], e[2], e[3]); o1 = vec4(e[4], e[5], e[6], e[7]); o2 = vec4(e[8], 1.0, 0.0, 0.0);
}`;

const FS_BEST9 = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uD0, uD1, uD2, uCenterTex;
uniform float uTop, uDeadband, uFineScale;
out vec4 o;
float par(float a, float b, float c){ float d = a - 2.0 * b + c; return d > 1e-12 ? clamp(0.5 * (a - c) / d, -0.5, 0.5) : 0.0; }
void main(){
    vec4 a = textureLod(uD0, vec2(0.5), uTop), b = textureLod(uD1, vec2(0.5), uTop), c2 = textureLod(uD2, vec2(0.5), uTop);
    float w = max(c2.g, 1e-9);
    float e[9];
    e[0] = a.x / w; e[1] = a.y / w; e[2] = a.z / w; e[3] = a.w / w; e[4] = b.x / w; e[5] = b.y / w; e[6] = b.z / w; e[7] = b.w / w; e[8] = c2.r / w;
    int bi = 0; float avg = 0.0;
    for (int k = 0; k < 9; k++) { avg += e[k]; if (e[k] < e[bi]) bi = k; }
    avg /= 9.0;
    int ix = bi % 3, iy = bi / 3;
    float fx = ix == 1 ? par(e[iy * 3], e[bi], e[iy * 3 + 2]) : 0.0;
    float fy = iy == 1 ? par(e[ix], e[bi], e[6 + ix]) : 0.0;
    vec2 c = floor(texelFetch(uCenterTex, ivec2(0), 0).xy * uFineScale + 0.5);
    vec2 s = (c + vec2(float(ix - 1) + fx, float(iy - 1) + fy)) * 2.0;
    if (length(s) < uDeadband) s = vec2(0.0);
    float q = avg > 1e-12 ? clamp((avg - e[bi]) / avg, 0.0, 1.0) : 0.0;
    o = vec4(s, q, e[bi]);
}`;

/* pick: of the two hypotheses' fine answers, the one with the lower cost (a tie keeps the first). */
const FS_PICK = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uA, uB;
out vec4 o;
void main(){
    vec4 a = texelFetch(uA, ivec2(0), 0), b = texelFetch(uB, ivec2(0), 0);
    o = b.w < a.w ? b : a;
}`;

/* the shift to read the history at, in full-size pixels. */
const SHIFT_DECL = `
uniform sampler2D uShiftTex; // 1×1: xy = shift at the fine search level (texels), z = quality
uniform float uShiftScale;   // full-size pixels per fine-level texel; 0 = no alignment
uniform float uShiftMinQ;
vec2 sceneShift(){
    if (uShiftScale <= 0.0) return vec2(0.0);
    vec4 s = texelFetch(uShiftTex, ivec2(0), 0);
    return s.z >= uShiftMinQ ? s.xy * uShiftScale : vec2(0.0);
}
`;

const FS_TEMPORAL = `#version 300 es
${COMMON}
${ACC_DECL}
${SHIFT_DECL}
uniform float uNMax, uGamma, uKappa, uZLo, uZHi, uFresh, uFrame;
uniform vec2 uNoise;         // σ²(L) = a + b·L
#ifdef ACC_UINT
layout(location = 0) out uvec4 oAcc;
#else
layout(location = 0) out vec4 oAcc;
#endif
layout(location = 1) out vec4 oAux;   // r motion, g √raw luma
void cr(float t, out float w[4]){ float t2 = t * t, t3 = t2 * t;
    w[0] = -0.5 * t3 + t2 - 0.5 * t; w[1] = 1.5 * t3 - 2.5 * t2 + 1.0; w[2] = -1.5 * t3 + 2.0 * t2 + 0.5 * t; w[3] = 0.5 * t3 - 0.5 * t2; }
void tb(float t, out float w[4]){ w[0] = 0.25 * (1.0 - t); w[1] = 0.25 * t + 0.5 * (1.0 - t); w[2] = 0.5 * t + 0.25 * (1.0 - t); w[3] = 0.25 * t; }
void store(vec3 rgb, float n){
#ifdef ACC_UINT
    float d = hash(gl_FragCoord.xy, uFrame) - 0.5;   // dithered rounding: an average of tiny steps does not stall
    oAcc = uvec4(uvec3(clamp(rgb * 65535.0 + 0.5 + d, 0.0, 65535.0)), uint(clamp(n * 256.0 + 0.5, 0.0, 65535.0)));
#else
    oAcc = vec4(rgb, n);
#endif
}
void main(){
    vec2 pc = floor(gl_FragCoord.xy) + 0.5;
    vec3 m = vec3(0.0), s2 = vec3(0.0), cur = vec3(0.0);
    float lc = 0.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
        vec3 v = srcLin(pc + vec2(float(i), float(j)));
        m += v; s2 += v * v;
        lc += luma(v) * (i == 0 ? 0.5 : 0.25) * (j == 0 ? 0.5 : 0.25);
        if (i == 0 && j == 0) cur = v;
    }
    m /= 9.0;
    vec3 sd = sqrt(max(s2 / 9.0 - m * m, 0.0)) + 1e-4;
    oAux = vec4(0.0, sqrt(clamp(luma(cur), 0.0, 1.0)), 0.0, 1.0);
    vec2 hp = pc - sceneShift();
    if (uFresh > 0.5 || uNMax <= 1.0 || hp.x < 0.0 || hp.y < 0.0 || hp.x > uSize.x || hp.y > uSize.y) { store(cur, 1.0); return; }
    vec2 f = hp - 0.5; vec2 fb = floor(f); vec2 t = f - fb; ivec2 b = ivec2(fb);
    float cx[4], cy[4], tx[4], ty[4];
    cr(t.x, cx); cr(t.y, cy); tb(t.x, tx); tb(t.y, ty);
    vec3 hc = vec3(0.0), lo = vec3(1e9), hi = vec3(-1e9);
    float lh = 0.0, n = 0.0;
    for (int ky = 0; ky < 4; ky++) for (int kx = 0; kx < 4; kx++) {
        vec4 a = accAt(b + ivec2(kx - 1, ky - 1));
        hc += a.rgb * (cx[kx] * cy[ky]);
        lh += luma(a.rgb) * (tx[kx] * ty[ky]);
        if (kx == 1 || kx == 2) if (ky == 1 || ky == 2) {
            lo = min(lo, a.rgb); hi = max(hi, a.rgb);
            n += a.a * (kx == 1 ? 1.0 - t.x : t.x) * (ky == 1 ? 1.0 - t.y : t.y);
        }
    }
    hc = clamp(hc, lo, hi);     // Catmull-Rom overshoot at hard edges
    float vp = max(uNoise.x + uNoise.y * max(lc, 0.0), ${PARAMS.noiseFloor.toExponential()});
    float z = abs(lc - lh) / sqrt(vp * 0.140625 * (1.0 + 1.0 / max(n, 1.0)));
    float mz = smoothstep(uZLo, uZHi, z);
    vec3 a = clamp(hc, m - uGamma * sd, m + uGamma * sd);
    vec3 dv = abs(hc - a) / sd;
    float d = max(dv.r, max(dv.g, dv.b));
    float nh = min(n * exp(-uKappa * d) * (1.0 - mz) + 1.0, uNMax);
    store(a + (cur - a) / nh, nh);
    oAux.r = max(mz, min(1.0, d * 0.5));
}`;

/* half: the averaged picture at half size — rgb linear + luma — then mipmapped. */
const FS_HALF = `#version 300 es
${COMMON}
${ACC_DECL}
out vec4 o;
void main(){
    ivec2 p = ivec2(gl_FragCoord.xy) * 2;
    vec3 c = (accAt(p).rgb + accAt(p + ivec2(1, 0)).rgb + accAt(p + ivec2(0, 1)).rgb + accAt(p + ivec2(1, 1)).rgb) * 0.25;
    vec4 v = vec4(c, luma(c));
#ifdef HALF_SQRT
    o = sqrt(max(v, 0.0));
#else
    o = v;
#endif
}`;

const FS_OUT = `#version 300 es
${COMMON}
${ACC_DECL}
${HALF_DECL}
uniform sampler2D uAux;      // r motion
uniform sampler2D uLut;      // 256×1 palette
uniform sampler2D uOverlay;  // HUD / reticle, premultiplied
uniform vec2 uNoise;
uniform float uDenoiseH, uGain, uTarget, uWhite, uLocal, uLocalLod, uDetail, uDesat;
uniform float uUseLut, uMotionTint, uSplit, uDither, uFrame, uTube, uGrain, uOverlayOn, uLocalMin, uLocalMax;
out vec4 o;
float tone(float x, float W){ if (x <= 0.0) return 0.0; return min(x * (1.0 + x / (W * W)) / (1.0 + x), 1.0); }
void main(){
    ivec2 ip = ivec2(int(gl_FragCoord.x), int(uSize.y) - 1 - int(gl_FragCoord.y));
    vec2 pc = vec2(ip) + 0.5;
    vec2 uv = pc / uSize;
    vec3 outc;
    if (pc.x < uSplit * uSize.x) {
        // «как видит камера»: the frame as it came, no gain, no filter
        outc = texture(uIn, srcUv(pc)).rgb;
    } else {
        vec4 c0 = accAt(ip);
        float Lc = luma(c0.rgb);
        float nh = max(c0.a, 1.0);
        float sig = sqrt(max(uNoise.x + uNoise.y * max(Lc, 0.0), ${PARAMS.noiseFloor.toExponential()}) / nh);
        vec3 rgb = c0.rgb; float Lb = Lc;
        if (uDenoiseH > 0.0) {
            float rr = uDenoiseH * sig, inv2r = 1.0 / (2.0 * rr * rr);
            float sw = 0.0; vec3 acc = vec3(0.0);
            for (int oy = -2; oy <= 2; oy++) for (int ox = -2; ox <= 2; ox++) {
                vec3 q = accAt(ip + ivec2(ox, oy)).rgb;
                float dl = luma(q) - Lc;
                float wt = exp(-float(ox * ox + oy * oy) * ${(1 / (2 * PARAMS.spatialSigma * PARAMS.spatialSigma)).toFixed(8)} - dl * dl * inv2r);
                sw += wt; acc += wt * q;
            }
            rgb = acc / sw; Lb = luma(rgb);
        }
        vec4 px = halfAt(uv, 0.0);
        if (uDetail > 0.0) {
            float amt = uDetail * smoothstep(2.0, 8.0, Lb / max(sig, 1e-6));
            float Ls = max(0.0, Lb + amt * (Lb - px.a));
            rgb *= Lb > 1e-6 ? Ls / Lb : 1.0; Lb = Ls;
        }
        float rel = sig / max(Lb, 1e-5);
        float cm = smoothstep(0.03, 0.25, rel);
        if (cm > 0.0 && px.a > 1e-6) {
            vec3 own = Lb > 1e-6 ? rgb / Lb : vec3(0.0);
            rgb = Lb * mix(own, px.rgb / px.a, cm);
        }
        float sat = 1.0 - uDesat * smoothstep(0.15, 0.6, rel);
        float Lloc = 0.5 * (halfAt(uv, uLocalLod).a + halfAt(uv, uLocalLod + 1.0).a);
        float gl = 1.0;
        if (uLocal > 0.0) gl = clamp(pow(max(Lloc * uGain, 1e-5) / uTarget, -uLocal), uLocalMin, uLocalMax);
        float X = Lb * uGain * gl;
        float Y = tone(X, uWhite);
        vec3 c = rgb * (Lb > 1e-7 ? Y / Lb : 0.0);
        c = Y + (c - Y) * sat;
        float mx = max(c.r, max(c.g, c.b));
        if (mx > 1.0) c = Y + (c - Y) * ((mx - Y) > 1e-6 ? (1.0 - Y) / (mx - Y) : 0.0);
        outc = l2s(c);
        if (uUseLut > 0.5) {
            float ys = l2s(vec3(Y)).r;
            if (uGrain > 0.0) ys = clamp(ys + uGrain * (hash(pc, uFrame) - 0.5) * (0.35 + ys), 0.0, 1.0);
            outc = texelFetch(uLut, ivec2(int(clamp(floor(ys * 255.0 + 0.5), 0.0, 255.0)), 0), 0).rgb;
        }
        if (uMotionTint > 0.0) {
            float mt = texelFetch(uAux, ip, 0).r * uMotionTint;
            outc += (vec3(1.0, 0.16, 0.1) - outc) * mt;
        }
    }
    if (uTube > 0.5) {
        // a tube's round field: one circle, or two overlapping for binoculars
        vec2 q = (pc - 0.5 * uSize) / (0.5 * uSize.y);
        float r = uTube > 1.5 ? min(length(q - vec2(-0.42, 0.0)), length(q - vec2(0.42, 0.0))) : length(q);
        float rad = uTube > 1.5 ? 0.86 : 0.97;
        float k = 1.0 - smoothstep(rad - 0.05, rad, r);
        outc *= k * (1.0 - 0.35 * smoothstep(0.2, rad, r));
    }
    if (uOverlayOn > 0.5) {
        vec4 ov = texture(uOverlay, uv);
        outc = outc * (1.0 - ov.a) + ov.rgb;
    }
    if (uDither > 0.0) outc += (hash(pc, uFrame) + hash(pc + 17.0, uFrame) - 1.0) / 255.0;
    o = vec4(clamp(outc, 0.0, 1.0), 1.0);
}`;

/*
 * stats: rows [0,GH) — the RAW camera bytes at the grid pixels (the noise
 * meter and the infrared test need single pixels, never averages);
 * rows [GH,2GH) — r √(averaged luma at lod 3), g motion, b history length / 128,
 * a camera shake this frame (|shift| / 16 px, up to 0.98; 1.0 = not measured).
 */
const FS_STATS = `#version 300 es
${COMMON}
${ACC_DECL}
${HALF_DECL}
${SHIFT_DECL}
uniform sampler2D uAux;
uniform vec2 uGrid;
uniform float uNMaxS;
out vec4 o;
void main(){
    ivec2 g = ivec2(gl_FragCoord.xy);
    int gh = int(uGrid.y);
    bool raw = g.y < gh;
    ivec2 cell = ivec2(g.x, raw ? g.y : g.y - gh);
    vec2 pc = (vec2(cell) + 0.5) / uGrid * uSize;
    pc = floor(pc) + 0.5;
    if (raw) {
        vec2 su = srcUv(pc);
        ivec2 ts = textureSize(uIn, 0);
        o = vec4(texelFetch(uIn, clamp(ivec2(su * vec2(ts)), ivec2(0), ts - 1), 0).rgb, 1.0);
    } else {
        float l = halfAt(pc / uSize, 3.0).a;
        vec4 ax = texelFetch(uAux, ivec2(pc), 0);
        float shake = uShiftScale > 0.0 ? min(length(sceneShift()) / 16.0, 0.98) : 1.0;
        o = vec4(sqrt(clamp(l, 0.0, 1.0)), ax.r, clamp(accAt(ivec2(pc)).a / 128.0, 0.0, 1.0), shake);
    }
}`;

/* A copy of the output into a 2D-readable frame (snapshot at the processing size). */

export class NightRenderer {
    /**
     * @param {HTMLCanvasElement} canvas the visible picture
     */
    constructor(canvas, opts) {
        this.canvas = canvas;
        this.opts = opts || {};
        this.gl = null;
        this.lost = false;
        this.W = 0; this.H = 0;
        this.frameNo = 0;
        this.fresh = true;
        this.params = Object.assign({
            stack: 12, denoise: 1.7, gain: 1, target: PARAMS.target, white: PARAMS.white, local: 0,
            detail: 0, desat: 0.35, look: 'color', motionTint: 0, split: 0, dither: 1, tube: 0, grain: 0,
            noise: { a: 5e-7, b: 2e-4 }, align: true, zLo: 2.5, zHi: 4.5, overlay: false,
        }, this.opts.params || {});
        this.grid = { w: 64, h: 36 };
        this._lutName = null;
        this._pending = [];
        this.stats = null;      // last read-back {raw:Uint8Array, avg:Uint8Array, w, h, frame}
        this._init();
        // opts.onContext('lost' | 'restored'): the page redraws what lives in textures it uploaded (the overlay)
        const told = (what) => { try { if (this.opts.onContext) this.opts.onContext(what); } catch (_) { } };
        canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.lost = true; this._mat = null; told('lost'); }, false);
        canvas.addEventListener('webglcontextrestored', () => { this.lost = false; this._init(); if (this.W) this.resize(this.W, this.H, true); told('restored'); }, false);
    }

    static supported() {
        try {
            const c = document.createElement('canvas');
            const gl = c.getContext('webgl2');
            const ok = !!gl;
            try { const l = gl && gl.getExtension('WEBGL_lose_context'); if (l) l.loseContext(); } catch (_) { }
            return ok;
        } catch (_) { return false; }
    }

    _init() {
        const gl = this.canvas.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
        if (!gl) throw new Error('webgl2-unavailable');
        this.gl = gl;
        // opts.storage = 'uint' forces the integer lane (the parity stand checks both)
        this.floatRT = this.opts.storage !== 'uint' && !!gl.getExtension('EXT_color_buffer_float');
        const defs = (this.floatRT ? '' : '#define ACC_UINT\n#define HALF_SQRT\n');
        const withDefs = (src) => src.replace('#version 300 es\n', '#version 300 es\n' + defs);
        this.vao = gl.createVertexArray();
        this.prog = {
            temporal: this._program(withDefs(FS_TEMPORAL)),
            half: this._program(withDefs(FS_HALF)),
            out: this._program(withDefs(FS_OUT)),
            stats: this._program(withDefs(FS_STATS)),
        };
        if (this.floatRT) {
            this.prog.curHalf = this._program(FS_CURHALF);
            this.prog.cost = this._program(FS_COST);
            this.prog.best = this._program(FS_BEST);
            this.prog.diff9 = this._program(FS_DIFF9);
            this.prog.best9 = this._program(FS_BEST9);
            this.prog.pick = this._program(FS_PICK);
        }
        this.texIn = this._tex(1, 1, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR);
        this.texLut = this._tex(256, 1, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
        this.texOverlay = this._tex(1, 1, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR);
        this._lutName = null;
        this._reads = [];   // in-flight read-backs: {buf, fence, frame, ready}
        this.fresh = true;
    }

    _program(fs) {
        const gl = this.gl;
        const sh = (type, src) => {
            const s = gl.createShader(type);
            gl.shaderSource(s, src); gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS) && !gl.isContextLost()) {
                const log = gl.getShaderInfoLog(s);
                throw new Error('shader: ' + log);
            }
            return s;
        };
        const p = gl.createProgram();
        gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
        gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
        gl.linkProgram(p);
        if (!gl.getProgramParameter(p, gl.LINK_STATUS) && !gl.isContextLost()) throw new Error('link: ' + gl.getProgramInfoLog(p));
        const u = {};
        const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
        for (let i = 0; i < n; i++) { const a = gl.getActiveUniform(p, i); const name = a.name.replace(/\[0\]$/, ''); u[name] = gl.getUniformLocation(p, name); }
        return { p, u };
    }

    _tex(w, h, ifmt, fmt, type, filter, mips) {
        const gl = this.gl;
        const t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mips ? gl.LINEAR_MIPMAP_LINEAR : filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        if (mips) {
            const levels = 1 + Math.floor(Math.log2(Math.max(w, h)));
            gl.texStorage2D(gl.TEXTURE_2D, levels, ifmt, w, h);
        } else {
            gl.texImage2D(gl.TEXTURE_2D, 0, ifmt, w, h, 0, fmt, type, null);
        }
        return t;
    }

    _fbo(textures) {
        const gl = this.gl;
        const f = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, f);
        const bufs = [];
        textures.forEach((t, i) => { gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, gl.TEXTURE_2D, t, 0); bufs.push(gl.COLOR_ATTACHMENT0 + i); });
        gl.drawBuffers(bufs);
        const st = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        if (st !== gl.FRAMEBUFFER_COMPLETE) throw new Error('fbo-incomplete-' + st);
        return f;
    }

    /** Processing size. Rebuilds the history (a new size has no past). */
    resize(W, H, force) {
        W = Math.max(16, Math.round(W)); H = Math.max(16, Math.round(H));
        if (!force && W === this.W && H === this.H) return;
        const gl = this.gl;
        this._freeSized();
        this.W = W; this.H = H;
        this.canvas.width = W; this.canvas.height = H;
        const accFmt = this.floatRT ? [gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT] : [gl.RGBA16UI, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT];
        this.acc = [0, 1].map(() => this._tex(W, H, accFmt[0], accFmt[1], accFmt[2], gl.NEAREST));
        this.aux = this._tex(W, H, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
        this.hw = Math.max(1, W >> 1); this.hh = Math.max(1, H >> 1);
        this.halfTex = this._tex(this.hw, this.hh, this.floatRT ? gl.RGBA16F : gl.RGBA8, gl.RGBA, this.floatRT ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE, gl.LINEAR, true);
        this.halfLevels = 1 + Math.floor(Math.log2(Math.max(this.hw, this.hh)));
        this.fboAcc = this.acc.map((t) => this._fbo([t, this.aux]));
        this.fboHalf = this._fbo([this.halfTex]);
        if (this.floatRT) {
            this.curHalf = this._tex(this.hw, this.hh, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.LINEAR, true);
            this.fboCurHalf = this._fbo([this.curHalf]);
            this.costA = this._tex(7, 7, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.costB = this._tex(3, 3, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.bestA = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.bestB = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboCostA = this._fbo([this.costA]); this.fboCostB = this._fbo([this.costB]);
            this.fboBestA = this._fbo([this.bestA]); this.fboBestB = this._fbo([this.bestB]);
            const pot = (n) => Math.max(16, 1 << Math.floor(Math.log2(Math.max(1, n))));
            this.dw = pot(this.hw); this.dh = pot(this.hh);
            this.diffTop = Math.floor(Math.log2(Math.max(this.dw, this.dh)));
            this.diff = [0, 1, 2].map(() => this._tex(this.dw, this.dh, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.LINEAR, true));
            this.fboDiff = this._fbo(this.diff);
            this.bestC = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestC = this._fbo([this.bestC]);
            this.bestD = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestD = this._fbo([this.bestD]);
            // the second hypothesis: its coarse start, its two fine answers, and the pick
            this.bestS = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestS = this._fbo([this.bestS]);
            this.bestC2 = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestC2 = this._fbo([this.bestC2]);
            this.bestD2 = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestD2 = this._fbo([this.bestD2]);
            this.bestF = this._tex(1, 1, gl.RGBA32F, gl.RGBA, gl.FLOAT, gl.NEAREST);
            this.fboBestF = this._fbo([this.bestF]);
        }
        this.statsTex = this._tex(this.grid.w, this.grid.h * 2, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
        this.fboStats = this._fbo([this.statsTex]);
        this._dropReads();
        this.cur = 0;
        this.fresh = true;
        this.localLod = localLevelFor(W, H);
    }

    _freeSized() {
        const gl = this.gl;
        if (!gl) return;
        const del = (t) => { if (t) gl.deleteTexture(t); };
        const delF = (f) => { if (f) gl.deleteFramebuffer(f); };
        (this.acc || []).forEach(del); (this.fboAcc || []).forEach(delF);
        [this.aux, this.halfTex, this.curHalf, this.costA, this.costB, this.bestA, this.bestB, this.bestC, this.bestD, this.bestS, this.bestC2, this.bestD2, this.bestF, this.statsTex].forEach(del);
        (this.diff || []).forEach(del);
        delF(this.fboDiff); delF(this.fboBestC); delF(this.fboBestD); delF(this.fboBestS); delF(this.fboBestC2); delF(this.fboBestD2); delF(this.fboBestF);
        [this.fboHalf, this.fboCurHalf, this.fboCostA, this.fboCostB, this.fboBestA, this.fboBestB, this.fboStats].forEach(delF);
        this.acc = this.fboAcc = null;
        this.aux = this.halfTex = this.curHalf = this.costA = this.costB = this.bestA = this.bestB = this.bestC = this.bestD = this.statsTex = null;
        this.diff = this.fboDiff = this.fboBestC = this.fboBestD = null;
        this.bestS = this.bestC2 = this.bestD2 = this.bestF = this.fboBestS = this.fboBestC2 = this.fboBestD2 = this.fboBestF = null;
        this.fboHalf = this.fboCurHalf = this.fboCostA = this.fboCostB = this.fboBestA = this.fboBestB = this.fboStats = null;
    }

    /** Read-back grid size: 64×36 normally, denser for the infrared test (a remote's LED is small). */
    setGrid(w, h) {
        if (w === this.grid.w && h === this.grid.h) return;
        const gl = this.gl;
        this.grid = { w, h };
        if (!gl || !this.W) return;
        if (this.statsTex) gl.deleteTexture(this.statsTex);
        if (this.fboStats) gl.deleteFramebuffer(this.fboStats);
        this.statsTex = this._tex(w, h * 2, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
        this.fboStats = this._fbo([this.statsTex]);
        this._dropReads();
        this.stats = null;
    }

    set(p) { Object.assign(this.params, p || {}); }
    reset() { this.fresh = true; }

    /** HUD/reticle layer at the processing size (a 2D canvas), or null to hide. */
    setOverlay(canvas2d) {
        const gl = this.gl;
        if (!gl || this.lost) return;
        if (!canvas2d) { this.params.overlay = false; return; }
        gl.bindTexture(gl.TEXTURE_2D, this.texOverlay);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, canvas2d);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        this.params.overlay = true;
    }

    _ensureLut() {
        const gl = this.gl, name = this.params.look;
        if (name === this._lutName) return;
        const lut = paletteLUT(name);
        if (lut) {
            gl.bindTexture(gl.TEXTURE_2D, this.texLut);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, lut);
        }
        this._lutName = name;
    }

    /**
     * Upload a frame. `src` is a <video>, a VideoFrame, an ImageBitmap or a
     * canvas. Returns false when there was no frame to take (an interrupted
     * camera: WebKit throws InvalidStateError exactly like drawImage does).
     */
    upload(src) {
        const gl = this.gl;
        if (!gl || this.lost) return false;
        if (src && src.tagName === 'VIDEO') {
            if (!(src.videoWidth > 0) || src.readyState < 2) return false;
            const so = src.srcObject;
            const tr = so && so.getVideoTracks ? so.getVideoTracks() : [];
            for (const t of tr) if (t.readyState === 'ended' || t.muted) return false;
        }
        gl.bindTexture(gl.TEXTURE_2D, this.texIn);
        try {
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, src);
        } catch (e) {
            if (e && (e.name === 'InvalidStateError' || e.code === 11)) return false;
            throw e;
        }
        return true;
    }

    /**
     * Run the passes for the uploaded frame. `srcMat` maps image uv (top-left
     * origin, of the processing frame) to source texture uv: zoom, mirror,
     * rotation. Column-major 3×3.
     */
    render(srcMat) {
        const gl = this.gl;
        if (!gl || this.lost || !this.W) return false;
        const P = this.params, W = this.W, H = this.H;
        this._ensureLut();
        gl.bindVertexArray(this.vao);
        gl.disable(gl.BLEND); gl.disable(gl.DEPTH_TEST);
        const mat = srcMat || IDENTITY;
        const prev = this.cur, next = 1 - this.cur;
        const fresh = this.fresh || P.stack <= 1;
        const align = this.floatRT && P.align && !fresh;

        // ── shake search
        if (align) {
            this._pass(this.prog.curHalf, this.fboCurHalf, this.hw, this.hh, (u) => {
                gl.uniform2f(u.uSize, W, H); gl.uniformMatrix3fv(u.uSrc, false, mat);
                this._bind(0, this.texIn, u.uIn);
            });
            gl.bindTexture(gl.TEXTURE_2D, this.curHalf); gl.generateMipmap(gl.TEXTURE_2D);
            const top = this.halfLevels - 1;
            const lodA = Math.min(alignLevels(this.hw, this.hh).a, top);
            // coarse: ±3 texels, no centre
            this._pass(this.prog.cost, this.fboCostA, 7, 7, (u) => {
                this._bind(0, this.curHalf, u.uCur); this._bind(1, this.halfTex, u.uPrev); this._bind(2, this.bestB, u.uCenterTex);
                gl.uniform1f(u.uUseCenter, 0); gl.uniform1f(u.uCenterScale, 1); gl.uniform1f(u.uLod, lodA); gl.uniform1i(u.uR, 3);
                gl.uniform1f(u.uTopCur, top); gl.uniform1f(u.uTopPrev, top); gl.uniform1f(u.uPrior, ALIGN_PRIOR);
            });
            this._pass(this.prog.best, this.fboBestA, 1, 1, (u) => { this._bind(0, this.costA, u.uCost); gl.uniform1i(u.uS, 7); gl.uniform1i(u.uSecond, 0); });
            // the runner-up from another basin: a repeating pattern matches a period away (core.js shiftSearch)
            this._pass(this.prog.best, this.fboBestS, 1, 1, (u) => { this._bind(0, this.costA, u.uCost); gl.uniform1i(u.uS, 7); gl.uniform1i(u.uSecond, 1); });
            // down the pyramid one level at a time: ±1 around the doubled winner (a ×4 jump lost the shift)
            const descend = (start) => {
                let best = start;
                for (let L = lodA - 1; L >= 1; L--) {
                    const from = best, to = from === this.bestA ? this.bestB : this.bestA;
                    this._pass(this.prog.cost, this.fboCostB, 3, 3, (u) => {
                        this._bind(0, this.curHalf, u.uCur); this._bind(1, this.halfTex, u.uPrev); this._bind(2, from, u.uCenterTex);
                        gl.uniform1f(u.uUseCenter, 1); gl.uniform1f(u.uCenterScale, 2); gl.uniform1f(u.uLod, L); gl.uniform1i(u.uR, 1);
                        gl.uniform1f(u.uTopCur, top); gl.uniform1f(u.uTopPrev, top); gl.uniform1f(u.uPrior, 0);
                    });
                    this._pass(this.prog.best, to === this.bestA ? this.fboBestA : this.fboBestB, 1, 1, (u) => { this._bind(0, this.costB, u.uCost); gl.uniform1i(u.uS, 3); gl.uniform1i(u.uSecond, 0); });
                    best = to;
                }
                return best;
            };
            const best = descend(this.bestA);
            // fine: ±1 at the half-size level, every pixel at once, reduced by mipmaps — twice,
            // the second window centred on the first one's winner (core.js shiftSearch)
            const fine = (center, scale, deadband, fbo) => {
                this._pass(this.prog.diff9, this.fboDiff, this.dw, this.dh, (u) => {
                    this._bind(0, this.curHalf, u.uCur); this._bind(1, this.halfTex, u.uPrev); this._bind(2, center, u.uCenterTex);
                    gl.uniform1f(u.uTopCur, top); gl.uniform1f(u.uTopPrev, top); gl.uniform1f(u.uTau2, ALIGN.tau * ALIGN.tau); gl.uniform2f(u.uDiffSize, this.dw, this.dh); gl.uniform1f(u.uFineScale, scale);
                });
                for (const t of this.diff) { gl.bindTexture(gl.TEXTURE_2D, t); gl.generateMipmap(gl.TEXTURE_2D); }
                this._pass(this.prog.best9, fbo, 1, 1, (u) => {
                    this._bind(0, this.diff[0], u.uD0); this._bind(1, this.diff[1], u.uD1); this._bind(2, this.diff[2], u.uD2); this._bind(3, center, u.uCenterTex);
                    gl.uniform1f(u.uTop, this.diffTop); gl.uniform1f(u.uDeadband, deadband); gl.uniform1f(u.uFineScale, scale);
                });
            };
            fine(best, 2, 0, this.fboBestC);                    // centre: the level-1 winner, in level-1 texels
            fine(this.bestC, 0.5, ALIGN.deadband, this.fboBestD); // centre: the first fine answer, in full-size pixels
            // the second hypothesis the same way (bestA/bestB are free again), then the lower fine cost wins
            const best2 = descend(this.bestS);
            fine(best2, 2, 0, this.fboBestC2);
            fine(this.bestC2, 0.5, ALIGN.deadband, this.fboBestD2);
            this._pass(this.prog.pick, this.fboBestF, 1, 1, (u) => { this._bind(0, this.bestD, u.uA); this._bind(1, this.bestD2, u.uB); });
            this._shiftScale = 1;
        }

        // ── temporal
        this._pass(this.prog.temporal, this.fboAcc[next], W, H, (u) => {
            gl.uniform2f(u.uSize, W, H); gl.uniformMatrix3fv(u.uSrc, false, mat);
            this._bind(0, this.texIn, u.uIn); this._bind(1, this.acc[prev], u.uAcc);
            if (this.floatRT) this._bind(2, this.bestF, u.uShiftTex);
            gl.uniform1f(u.uShiftScale, align ? this._shiftScale : 0);
            gl.uniform1f(u.uShiftMinQ, 0.02);
            gl.uniform1f(u.uNMax, Math.max(1, P.stack)); gl.uniform1f(u.uGamma, PARAMS.clipGamma); gl.uniform1f(u.uKappa, PARAMS.historyKappa);
            gl.uniform1f(u.uZLo, P.zLo); gl.uniform1f(u.uZHi, P.zHi); gl.uniform1f(u.uFresh, fresh ? 1 : 0); gl.uniform1f(u.uFrame, this.frameNo % 997);
            gl.uniform2f(u.uNoise, P.noise.a, P.noise.b);
        });
        this.cur = next;
        this.fresh = false;

        // ── half + mips
        this._pass(this.prog.half, this.fboHalf, this.hw, this.hh, (u) => {
            gl.uniform2f(u.uSize, W, H);
            this._bind(1, this.acc[next], u.uAcc);
        });
        gl.bindTexture(gl.TEXTURE_2D, this.halfTex); gl.generateMipmap(gl.TEXTURE_2D);

        // ── output
        this._mat = mat;
        this._out(mat, P);

        // ── stats (async read-back)
        this._collect();
        this._pass(this.prog.stats, this.fboStats, this.grid.w, this.grid.h * 2, (u) => {
            gl.uniform2f(u.uSize, W, H); gl.uniformMatrix3fv(u.uSrc, false, mat);
            this._bind(0, this.texIn, u.uIn); this._bind(1, this.acc[next], u.uAcc); this._bind(2, this.halfTex, u.uHalf); this._bind(3, this.aux, u.uAux);
            if (this.floatRT) this._bind(4, this.bestF, u.uShiftTex);
            gl.uniform1f(u.uShiftScale, align ? this._shiftScale : 0); gl.uniform1f(u.uShiftMinQ, 0.02);
            gl.uniform2f(u.uGrid, this.grid.w, this.grid.h); gl.uniform1f(u.uNMaxS, Math.max(1, P.stack));
        });
        // a fresh buffer per read-back, written once and read once (the MDN
        // async-readback recipe): a reused buffer made Chrome discard its
        // readback shadow and warn on every frame, whatever the order (23.10.2026)
        if (this._reads.length < 3) {
            const n = this.grid.w * this.grid.h * 2 * 4;
            const buf = gl.createBuffer();
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboStats);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buf);
            gl.bufferData(gl.PIXEL_PACK_BUFFER, n, gl.STREAM_READ);
            gl.readPixels(0, 0, this.grid.w, this.grid.h * 2, gl.RGBA, gl.UNSIGNED_BYTE, 0);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
            this._reads.push({ buf, fence: gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0), frame: this.frameNo, n, gw: this.grid.w, gh: this.grid.h });
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.flush();
        this.frameNo++;
        return true;
    }

    /** The output pass over the current history (the camera frame in texIn, the history in acc[cur]). */
    _out(mat, P) {
        const gl = this.gl, W = this.W, H = this.H;
        this._pass(this.prog.out, null, W, H, (u) => {
            gl.uniform2f(u.uSize, W, H); gl.uniformMatrix3fv(u.uSrc, false, mat);
            this._bind(0, this.texIn, u.uIn); this._bind(1, this.acc[this.cur], u.uAcc); this._bind(2, this.halfTex, u.uHalf);
            this._bind(3, this.aux, u.uAux); this._bind(4, this.texLut, u.uLut); this._bind(5, this.texOverlay, u.uOverlay);
            gl.uniform2f(u.uNoise, P.noise.a, P.noise.b);
            gl.uniform1f(u.uDenoiseH, P.denoise); gl.uniform1f(u.uGain, P.gain); gl.uniform1f(u.uTarget, P.target);
            gl.uniform1f(u.uWhite, P.white); gl.uniform1f(u.uLocal, P.local); gl.uniform1f(u.uLocalLod, this.localLod);
            gl.uniform1f(u.uLocalMin, PARAMS.localMin); gl.uniform1f(u.uLocalMax, PARAMS.localMax);
            gl.uniform1f(u.uDetail, P.detail); gl.uniform1f(u.uDesat, P.desat);
            gl.uniform1f(u.uUseLut, usesLut(P.look) ? 1 : 0); gl.uniform1f(u.uMotionTint, P.motionTint);
            gl.uniform1f(u.uSplit, P.split); gl.uniform1f(u.uDither, P.dither); gl.uniform1f(u.uFrame, this.frameNo % 997);
            gl.uniform1f(u.uTube, P.tube); gl.uniform1f(u.uGrain, P.grain); gl.uniform1f(u.uOverlayOn, P.overlay ? 1 : 0);
        });
    }

    /**
     * The output pass again with some parameters changed, the history untouched:
     * a photo without the compare line while the screen shows it. Same task as
     * snapshot() — the drawing buffer is not kept past it.
     */
    redraw(over) {
        const gl = this.gl;
        if (!gl || this.lost || !this.W || !this._mat) return false;
        gl.bindVertexArray(this.vao);
        gl.disable(gl.BLEND); gl.disable(gl.DEPTH_TEST);
        this._ensureLut();
        this._out(this._mat, Object.assign({}, this.params, over || {}));
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return true;
    }

    /** Pick up finished read-backs (oldest first) into this.stats. */
    _collect() {
        const gl = this.gl;
        while (this._reads.length) {
            const r = this._reads[0];
            if (gl.getSyncParameter(r.fence, gl.SYNC_STATUS) !== gl.SIGNALED) break;
            this._reads.shift();
            gl.deleteSync(r.fence);
            const out = new Uint8Array(r.n);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, r.buf);
            gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, out);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
            gl.deleteBuffer(r.buf);
            const half = r.n / 2;
            this.stats = { raw: out.subarray(0, half), avg: out.subarray(half), w: r.gw, h: r.gh, frame: r.frame };
        }
    }
    _dropReads() {
        const gl = this.gl;
        for (const r of this._reads || []) { try { gl.deleteSync(r.fence); gl.deleteBuffer(r.buf); } catch (_) { } }
        this._reads = [];
    }

    _bind(unit, tex, loc) {
        const gl = this.gl;
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        if (loc) gl.uniform1i(loc, unit);
    }

    _pass(prog, fbo, w, h, setup) {
        const gl = this.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.viewport(0, 0, w, h);
        gl.useProgram(prog.p);
        setup(prog.u);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    /**
     * The picture as it is on the canvas now, into a 2D canvas of the same
     * size — called right after render(), in the same task (the drawing
     * buffer is not preserved past it).
     */
    snapshot(target) {
        const c = target || document.createElement('canvas');
        c.width = this.W; c.height = this.H;
        const x = c.getContext('2d');
        x.drawImage(this.canvas, 0, 0);
        return c;
    }

    destroy() {
        const gl = this.gl;
        if (!gl) return;
        this._freeSized();
        try { const l = gl.getExtension('WEBGL_lose_context'); if (l) l.loseContext(); } catch (_) { }
        this.gl = null;
    }
}

function usesLut(name) { return name !== 'color' && !!paletteLUT(name); }

const IDENTITY = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
/** Shake search constants — the same as core.js shiftSearch({fine:true}). */
const ALIGN = { tau: 1, deadband: 0.25 };

/**
 * uv transform: digital zoom about a centre, mirror, and a quarter-turn
 * rotation of the source. Column-major for uniformMatrix3fv.
 */
export function sourceMatrix(o) {
    const z = Math.max(1, o.zoom || 1);
    const cx = o.cx == null ? 0.5 : o.cx, cy = o.cy == null ? 0.5 : o.cy;
    // image uv → zoomed uv
    let a = 1 / z, b = 0, c = 0, d = 1 / z;
    let tx = cx - 0.5 / z, ty = cy - 0.5 / z;
    if (o.mirror) { a = -a; tx = tx + 1 / z; }
    // rotation of the source by r quarter turns (clockwise as displayed)
    const r = ((o.rotate || 0) / 90) & 3;
    const M = [a, b, 0, c, d, 0, tx, ty, 1];   // [col0, col1, col2]
    if (!r) return new Float32Array(M);
    // display uv (u,v) → coded uv: r=1 (90°): (v, 1−u); r=2: (1−u, 1−v); r=3: (1−v, u)
    const R = r === 1 ? [0, -1, 0, 1, 0, 0, 0, 1, 1] : r === 2 ? [-1, 0, 0, 0, -1, 0, 1, 1, 1] : [0, 1, 0, -1, 0, 0, 1, 0, 1];
    return new Float32Array(mul3(R, M));
}
function mul3(A, B) {
    const o = new Array(9);
    for (let col = 0; col < 3; col++) for (let row = 0; row < 3; row++) {
        let s = 0;
        for (let k = 0; k < 3; k++) s += A[k * 3 + row] * B[col * 3 + k];
        o[col * 3 + row] = s;
    }
    return o;
}
